import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ReactMarkdown from 'react-markdown';

import { ChatService } from '../../../services/chatService';
import { useRequireSignIn } from '../../../components/auth/useRequireSignIn';
import { useCredits } from '../../../credits/useCredits';
import { OperationService } from '../../../services';
import { useOllamaInstalledModels, useProviderCatalog } from '../../../queries';
import {
  appendToken,
  buildChatContext,
  buildHistoryForBackend,
  chatHistoryStorageKey,
  finalizeAssistant,
  finalizeAssistantWithImages,
  loadConversation,
  markAssistantError,
  saveConversation,
  startTurn,
  type ChatImage,
  type ChatMessage,
} from './agentBarHelpers';
import {
  ProviderModelSelect,
  defaultProviderAndModel,
  isImageProviderKey,
} from '../../../components/ProviderModelSelect';
import { useAgentModelStore } from '../../../store/agentModelStore';
import { modelStillOffered } from './modelOptions';
import { VoiceMicButton, isVoiceInputEnabled } from './VoiceMicButton';
import { AuthedImage } from './AuthedImage';
import { ProviderStatusIndicator } from '../../../shell/ProviderStatusIndicator';

const COLLAPSED_HEIGHT = 56;
const EXPANDED_HEIGHT = 420;

/**
 * Agent Bar — F16a + model-selector pass. Collapsible chat surface
 * wired to the BE chat SSE endpoint (B24a). Provider + model are now
 * separate selectors: provider is a small dropdown; the model selector
 * is an Autocomplete for Ollama (combining live `/api/tags` + curated
 * cloud-tag catalog + freeSolo input for `:cloud` variants that don't
 * appear in `/api/tags`), and a plain Select for Gemini.
 */
interface AgentBarProps {
  sessionId?: string;
  /** Lab the bar is mounted in. Used for the per-studio conversation key
   *  and the chat context grounding. */
  labId?: string;
  labName?: string;
  studioId?: string;
  studioName?: string;
  /** Active operation kind in the right rail. Threaded into the chat
   *  context so the LLM knows what the user is currently working with. */
  activeOperationKind?: string;
  /**
   * Elevation pass 2026-05-26 (Area 3) — richer chat context. ImageLab
   * supplies live snapshots of the user's swatch picks, CSS-filter sliders,
   * carousel selection, and op-panel parameters so the LLM can answer
   * "why is my X failing" / "what did I just pick" / "tweak the filters
   * for me" with situational awareness instead of guessing. All optional;
   * WriterLab passes none of these (its context is text-only). The BE
   * `ChatController` formats them into a "Current user context" prelude
   * on the system prompt.
   */
  swatchSelections?: Record<string, string>;
  filterState?: Record<string, number> | null;
  activeAssetId?: string;
  activeOperationParams?: Record<string, unknown>;
  /**
   * Studio default LLM model id. Seeded into the picker on first load when
   * no user selection is persisted. User-picked values take precedence;
   * see `agentModelStore` for the precedence chain. (Writer's Lab uses
   * this to start each studio with a sensible default; Image Lab still
   * passes undefined and gets the catalog-derived ollama default.)
   */
  defaultModel?: string;
  /**
   * Snapshot getter for the current canvas document text. Called once per
   * outgoing chat message so the LLM can answer "critique what I just
   * wrote" / "tighten this paragraph" / "summarize the post above"
   * without the user having to re-paste. Writer's Lab supplies a Lexical-
   * editor reader (see WriterLab's wrapper around AgentBar); Image Lab
   * leaves this unset because its canvas isn't text. Return `undefined`
   * or empty string to skip — the BE drops empty document context.
   */
  getCurrentDocument?: () => string | undefined;
  /**
   * Lazily resolve (creating if needed) an image-generation session id. Image
   * Lab passes its `useImageLabSession.ensureSession` so agent-bar generations
   * attach to the active session/history. Labs without an image session leave
   * this unset — generation then runs session-less (the image still returns
   * and renders inline; it just isn't tied to a session timeline).
   */
  ensureImageSession?: () => Promise<string>;
}

export const AgentBar: React.FC<AgentBarProps> = ({
  sessionId,
  labId,
  labName,
  studioId,
  studioName,
  activeOperationKind,
  swatchSelections,
  filterState,
  activeAssetId,
  activeOperationParams,
  defaultModel,
  getCurrentDocument,
  ensureImageSession,
}) => {
  const storageKey = useMemo(() => chatHistoryStorageKey(labId, studioId), [labId, studioId]);
  const store = useMemo(() => (typeof window === 'undefined' ? null : window.localStorage), []);

  const [expanded, setExpanded] = useState(false);
  // Seed messages from persisted state for this lab/studio. Re-runs only when
  // the storage key changes (lab/studio switch), so within-session edits keep
  // the in-memory state authoritative.
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    storageKey ? loadConversation(storageKey, store) : [],
  );
  const [input, setInput] = useState('');
  // Provider+model are persisted via Zustand so operation builders + the
  // outline-gen request reads the same selection without prop-drilling.
  // We seed local state from the store on first render, write through on
  // every change, and prefer (in order): persisted store → studio default
  // → catalog default (ollama / llama3.2 / etc).
  const persistedSelection = useAgentModelStore(s => s.selection);
  const [provider, setProvider] = useState<string>(() => persistedSelection?.provider ?? '');
  const [model, setModel] = useState<string>(() => persistedSelection?.model ?? '');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const { requireSignIn } = useRequireSignIn();
  const { handleInsufficientCredits } = useCredits();
  const handleSubmitRef = useRef<((resubmit?: unknown) => Promise<void>) | null>(null);

  const catalog = useProviderCatalog();

  // A stored pick outlives the catalog. Google retired the Gemini 2.0 family, and a
  // browser that selected one kept sending it with every operation — which the provider
  // answered with a 404. Drop a selection the catalog no longer offers so the seeding
  // below replaces it.
  useEffect(() => {
    if (!catalog.data || !provider || !model) return;
    const entries = [...catalog.data.llm, ...catalog.data.image];
    if (modelStillOffered(entries, provider, model)) return;
    setProvider('');
    setModel('');
  }, [catalog.data, provider, model]);

  useEffect(() => {
    if (provider || !catalog.data) return;
    // Studio's `defaultLlmModel` takes priority over the catalog default —
    // find which provider owns it.
    if (defaultModel) {
      const owner = catalog.data.llm.find(e => e.models.some(m => m.id === defaultModel));
      if (owner) {
        setProvider(owner.key);
        setModel(defaultModel);
        return;
      }
    }
    const seed = defaultProviderAndModel(catalog.data.llm, 'ollama');
    setProvider(seed.provider);
    setModel(seed.model || 'llama3.2');
  }, [catalog.data, provider, defaultModel]);

  // Mirror local state into the shared store so operation builders read it.
  useEffect(() => {
    if (provider && model) {
      useAgentModelStore.getState().setSelection({ provider, model });
    }
  }, [provider, model]);
  // When the user switches provider, swap model to that provider's first.
  // Search both chat (llm) and image providers — the agent bar offers both.
  const handleProviderChange = useCallback(
    (next: string) => {
      setProvider(next);
      const entry = [
        ...(catalog.data?.llm ?? []),
        ...(catalog.data?.image ?? []),
      ].find(e => e.key === next);
      const seedModel =
        entry?.models[0]?.id ??
        (next === 'ollama' ? 'llama3.2' : next === 'gemini' ? 'gemini-2.5-flash' : '');
      setModel(seedModel);
    },
    [catalog.data],
  );

  // True when the selected provider is an image-generation provider (e.g.
  // fal.ai). In that mode a sent message is treated as an image prompt rather
  // than an LLM chat turn.
  const imageMode = useMemo(
    () => isImageProviderKey(catalog.data, provider),
    [catalog.data, provider],
  );

  // Studio / lab change → switch conversations: load the destination's
  // persisted thread (or empty), and abort any in-flight stream on the
  // outgoing thread so it doesn't bleed into the new one.
  const prevKeyRef = useRef<string | null>(storageKey);
  useEffect(() => {
    if (prevKeyRef.current === storageKey) return;
    prevKeyRef.current = storageKey;
    if (isStreaming) abortRef.current?.abort();
    setMessages(storageKey ? loadConversation(storageKey, store) : []);
    setStreamError(null);
    // Intentionally omit isStreaming from deps — only the key matters here;
    // the abort is best-effort cleanup for the side effect.
     
  }, [storageKey, store]);

  // Persist on every message change. Cheap: serialization is small and
  // localStorage is sync. Skipped while a turn is mid-stream (saveConversation
  // drops streaming entries anyway, but we avoid the write per-token).
  useEffect(() => {
    if (!storageKey) return;
    if (isStreaming) return;
    saveConversation(storageKey, messages, store);
  }, [storageKey, messages, store, isStreaming]);

  // Used only for the inline "N local / daemon down" status badge that sits
  // next to the model selector for the Ollama provider; the selector itself
  // pulls installed models internally via ProviderModelSelect.
  const ollamaModelsQuery = useOllamaInstalledModels();
  const ollamaInstalledCount = ollamaModelsQuery.data?.models.length ?? 0;
  const ollamaDaemonReachable = ollamaModelsQuery.data?.daemonReachable ?? false;

  const abortRef = useRef<AbortController | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = messageListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // `resubmit` lets the credit top-up flow re-run a prompt after payment. It is
  // typed loosely because this is also wired straight to onClick, which would
  // otherwise hand us a MouseEvent as the "text".
  const handleSubmit = useCallback(async (resubmit?: unknown) => {
    const trimmed = (typeof resubmit === 'string' ? resubmit : input).trim();
    if (!trimmed || isStreaming) return;
    // Chat and generation both spend credits. Checked before the input clears,
    // so a visitor who signs in doesn't lose what they typed.
    if (!requireSignIn()) return;

    setStreamError(null);
    setInput('');

    const { next, assistantId } = startTurn(messages, trimmed);
    setMessages(next);
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    // Image-generation mode: route the message to fal.ai (or whichever image
    // provider is selected) via the operations endpoint instead of LLM chat.
    // The model id alone routes the provider on the BE
    // (IProviderFactory.GetImageProviderForModel), so no provider field is sent.
    if (imageMode) {
      try {
        const generationSessionId = ensureImageSession ? await ensureImageSession() : undefined;
        let result = await OperationService.generate({
          prompt: trimmed,
          model: model || undefined,
          sessionId: generationSessionId,
          numImages: 1,
        });
        // The generate call usually blocks until terminal, but fall back to
        // polling if the BE returned an async (non-terminal) job with no images.
        if (
          !result.results?.images?.length &&
          result.status !== 'Failed' &&
          result.status !== 'Cancelled' &&
          result.operationId
        ) {
          const status = await OperationService.pollOperationStatus(result.operationId);
          result = { ...result, status: status.status, results: status.results, error: status.error };
        }
        if (result.status === 'Failed' || result.status === 'Cancelled' || result.error) {
          throw new Error(result.error?.message ?? 'Image generation failed');
        }
        const images: ChatImage[] = (result.results?.images ?? []).map(im => ({
          url: im.signedUrl ?? im.url,
          width: im.width,
          height: im.height,
        }));
        if (images.length === 0) throw new Error('No image was returned');
        setMessages(prev => finalizeAssistantWithImages(prev, assistantId, images));
      } catch (err) {
        const aborted = err instanceof DOMException && err.name === 'AbortError';
        // Out of credit opens the top-up drawer and re-submits this prompt once
        // it's paid — so put the text back rather than showing an error for
        // something the user is about to resolve.
        const toppingUp = handleInsufficientCredits(err, () =>
          void handleSubmitRef.current?.(trimmed)
        );
        if (aborted || toppingUp) {
          setMessages(prev => finalizeAssistant(prev, assistantId));
        } else {
          const msg = err instanceof Error ? err.message : 'Image generation failed';
          setMessages(prev => markAssistantError(prev, assistantId, msg));
          setStreamError(msg);
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
      return;
    }

    try {
      await ChatService.sendMessage(
        {
          message: trimmed,
          sessionId,
          studioId,
          context: buildChatContext({
            provider,
            model,
            history: buildHistoryForBackend(messages),
            lab: { id: labId, name: labName },
            studio: { id: studioId, name: studioName },
            activeOperationKind,
            swatchSelections,
            filterState,
            activeAssetId,
            activeOperationParams,
            currentDocument: getCurrentDocument?.(),
          }),
        },
        {
          signal: controller.signal,
          onChunk: chunk => {
            if (chunk.type === 'token' && chunk.content) {
              setMessages(prev => appendToken(prev, assistantId, chunk.content!));
            } else if (chunk.type === 'error' && chunk.content) {
              setMessages(prev => markAssistantError(prev, assistantId, chunk.content!));
            }
          },
        },
      );
      setMessages(prev => finalizeAssistant(prev, assistantId));
    } catch (err) {
      // AbortError isn't a real failure — the user pressed Stop. Finalize
      // the assistant turn at whatever content has accumulated rather than
      // marking it as errored.
      const aborted = err instanceof DOMException && err.name === 'AbortError';
      if (aborted) {
        setMessages(prev => finalizeAssistant(prev, assistantId));
      } else {
        const msg = err instanceof Error ? err.message : 'Chat request failed';
        setMessages(prev => markAssistantError(prev, assistantId, msg));
        setStreamError(msg);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [
    input,
    isStreaming,
    requireSignIn,
    messages,
    provider,
    model,
    sessionId,
    studioId,
    labId,
    labName,
    studioName,
    activeOperationKind,
    swatchSelections,
    filterState,
    activeAssetId,
    activeOperationParams,
    getCurrentDocument,
    imageMode,
    ensureImageSession,
  ]);

  const handleAbort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Kept in a ref so the top-up retry always calls the current closure — the
  // callback is stashed when the 402 lands and fires minutes later.
  handleSubmitRef.current = handleSubmit;

  const handleClear = useCallback(() => {
    if (isStreaming) abortRef.current?.abort();
    setMessages([]);
    setStreamError(null);
  }, [isStreaming]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSubmit();
      }
    },
    [handleSubmit],
  );

  return (
    <Box
      sx={{
        height: expanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        transition: 'height 0.18s ease',
      }}
      aria-label="Agent bar"
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, py: 0.75, height: COLLAPSED_HEIGHT, flexShrink: 0 }}
      >
        <Tooltip title={expanded ? 'Collapse chat' : 'Expand chat'}>
          <IconButton size="small" onClick={() => setExpanded(e => !e)} aria-label="Toggle chat">
            {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Tooltip>
        <SmartToyIcon fontSize="small" sx={{ color: 'text.secondary' }} />

        {/* Provider + model — driven by /api/v3/providers/catalog so any
            registered LLM provider shows up (no FE-side hardcoded list). */}
        <ProviderModelSelect
          kind="llm"
          provider={provider}
          model={model}
          onProviderChange={handleProviderChange}
          onModelChange={setModel}
          variant="inline"
          size="small"
          disabled={isStreaming}
          includeImageProviders
        />

        {provider === 'ollama' && (
          <Tooltip
            title={
              ollamaDaemonReachable
                ? `Local Ollama daemon: ${ollamaInstalledCount} model${ollamaInstalledCount === 1 ? '' : 's'} installed`
                : 'Local Ollama daemon not reachable — only `:cloud` tagged models will work'
            }
          >
            <Typography
              variant="caption"
              sx={{ color: ollamaDaemonReachable ? 'success.main' : 'warning.main', whiteSpace: 'nowrap' }}
            >
              {ollamaDaemonReachable ? `${ollamaInstalledCount} local` : 'daemon down'}
            </Typography>
          </Tooltip>
        )}

        {/* Provider health — reclaimed from the old fixed bottom-left overlay,
            now inline where the LLM chat + model selector live. */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', pl: 1, minWidth: 0 }}>
          <ProviderStatusIndicator />
        </Box>
        {messages.length > 0 && (
          <Tooltip title="Clear conversation">
            <IconButton size="small" onClick={handleClear} aria-label="Clear conversation">
              <DeleteSweepIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Box
        sx={{
          flex: 1,
          display: expanded ? 'flex' : 'none',
          flexDirection: 'column',
          minHeight: 0,
          borderTop: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          ref={messageListRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {messages.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mt: 2 }}>
              {imageMode
                ? 'Describe an image — the selected model will generate it here.'
                : "Ask the agent anything — it has read access to this lab's studio."}
            </Typography>
          )}
          {messages.map(m => (
            <MessageRow key={m.id} message={m} />
          ))}
        </Box>

        {streamError && (
          <Alert severity="error" onClose={() => setStreamError(null)} sx={{ mx: 2, mb: 1 }}>
            {streamError}
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-end"
          sx={{ p: 1.5, borderTop: theme => `1px solid ${theme.palette.divider}` }}
        >
          <TextField
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isStreaming
                ? imageMode
                  ? 'Generating…'
                  : 'Streaming…'
                : imageMode
                  ? 'Describe an image to generate…'
                  : 'Ask the agent…'
            }
            multiline
            maxRows={4}
            disabled={isStreaming}
            fullWidth
            size="small"
            inputProps={{ 'aria-label': 'Chat input' }}
          />
          {/* Spike 1 §1.4 — voice mic. On stop, the transcript is appended to
              the input field so the user can review / edit before sending.
              Hidden until the API serves /api/v3/voice/transcribe. */}
          {isVoiceInputEnabled() && (
            <VoiceMicButton
              disabled={isStreaming}
              onTranscript={text => setInput(prev => (prev ? `${prev} ${text}` : text))}
            />
          )}
          {isStreaming ? (
            <Tooltip title="Stop generating">
              <IconButton color="error" onClick={handleAbort} aria-label="Stop generating">
                <StopCircleIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              color="primary"
              onClick={handleSubmit}
              disabled={!input.trim()}
              aria-label="Send"
            >
              <SendIcon />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

const MessageRow: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
    >
      {!isUser && (
        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
          <SmartToyIcon sx={{ fontSize: 18 }} />
        </Avatar>
      )}
      <Paper
        elevation={0}
        sx={{
          px: 1.5,
          py: 1,
          maxWidth: '78%',
          borderRadius: 2,
          bgcolor: isUser ? 'primary.main' : 'action.hover',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          border: message.isError ? theme => `1px solid ${theme.palette.error.main}` : 'none',
          '& p': { m: 0 },
          '& p + p': { mt: 1 },
          '& code': { fontFamily: 'monospace', fontSize: '0.85em' },
          // Code blocks were a fixed near-black slab in BOTH modes (`#000` /
          // `#0b0e16`, always with `#eee` text) — right on a dark ground,
          // and a hole punched through the bubble on a light one, which no
          // theme had exercised until a light-first default shipped. Dark is
          // left byte-identical; only the light arm becomes theme-derived.
          '& pre': {
            bgcolor: theme =>
              theme.palette.mode === 'dark'
                ? '#000'
                : alpha(theme.palette.text.primary, 0.07),
            color: theme => (theme.palette.mode === 'dark' ? '#eee' : theme.palette.text.primary),
            border: theme =>
              theme.palette.mode === 'dark' ? 'none' : `1px solid ${theme.palette.divider}`,
            p: 1,
            borderRadius: 1,
            overflowX: 'auto',
          },
        }}
      >
        {isUser ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
        ) : (
          <Box sx={{ fontSize: '0.875rem' }}>
            <ReactMarkdown>{message.content || ' '}</ReactMarkdown>
            {message.isStreaming && (
              <Typography component="span" variant="caption" sx={{ opacity: 0.6, ml: 0.5 }}>
                ▍
              </Typography>
            )}
          </Box>
        )}
        {message.images && message.images.length > 0 && (
          <Stack spacing={1} sx={{ mt: message.content ? 1 : 0 }}>
            {message.images.map((img, i) => (
              <AuthedImage key={`${message.id}-img-${i}`} url={img.url} />
            ))}
          </Stack>
        )}
      </Paper>
      {isUser && (
        <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>
          <PersonIcon sx={{ fontSize: 18 }} />
        </Avatar>
      )}
    </Stack>
  );
};

